import { Mutation } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RemoveExternalAccount from 'services/graphql/mutations/RemoveExternalAccount';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useMutation } from 'urql';

interface useDeleteExternalAccountProps {
  accountId?: string | undefined;
  setDialogVisible: (val: boolean) => void;
  isRouteManageExternal?: boolean;
  externalAccountId?: string | undefined;
}
const useDeleteExternalAccount = ({
  setDialogVisible,
  accountId,
  isRouteManageExternal,
  externalAccountId,
}: useDeleteExternalAccountProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation(['accounts', 'myVaro']);

  const [
    {
      data: removeAccountData,
      fetching: removeAccountLoading,
      error: removeAccountError,
    },
    executeMutation,
  ] = useMutation<Mutation>(RemoveExternalAccount);

  const {
    refreshTiles,
    fetching: refreshTilesFetching,
    error: refreshTilesError,
    data: refreshTilesData,
  } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
  ]);

  const removeExternalAccount = async () => {
    await executeMutation({
      idempotencyId: getUuid(),
      id: isRouteManageExternal ? externalAccountId : (accountId as string),
    });
    refreshTiles();
  };

  const dialogButtons = [
    {
      label: translatedText('manageAccount.dialog.buttons.cancel'),
      onClick: () => {
        setDialogVisible(false);
      },
    },
    {
      label: translatedText('manageAccount.dialog.buttons.remove'),
      onClick: async () => {
        setDialogVisible(false);
        await removeExternalAccount();
      },
    },
  ];

  useEffect(() => {
    if (!removeAccountLoading && removeAccountData) {
      if (
        isRouteManageExternal &&
        removeAccountData?.removeExternalAccount === externalAccountId
      ) {
        if (!refreshTilesFetching && refreshTilesData) {
          router.dismissTo('/myvaro/external-accounts');
        }
      } else if (removeAccountData?.removeExternalAccount === accountId) {
        if (!refreshTilesFetching && refreshTilesData) {
          router.dismissTo('/'); //redirect to home Page
        }
      }
    }
  }, [
    accountId,
    externalAccountId,
    isRouteManageExternal,
    removeAccountData,
    removeAccountLoading,
    refreshTilesFetching,
    refreshTilesError,
    refreshTilesData,
    router,
  ]);

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const buttons = useMemo(
    () => ({
      primaryButton: {
        title: cancelButtonTitle,
        onPress: () => router.back(),
      },
    }),
    [cancelButtonTitle, router],
  );

  const removeAccountErrorModel = useMemo(() => {
    return removeAccountError?.message
      ? {
          screenType: ErrorScreenType.Error,
          messageHeadline: defaultTitle,
          messageBody: removeAccountError.message ?? defaultMessage,
          ...buttons,
        }
      : null;
  }, [removeAccountError, defaultTitle, defaultMessage, buttons]);

  return {
    dialogButtons,
    removeExternalAccount,
    removeAccountErrorModel,
    removeAccountLoading: refreshTilesFetching || removeAccountLoading,
  };
};

export default useDeleteExternalAccount;
